// The app shell: one phone-width column and the routes of this cycle.
//
// Three tabs under the navigation pill, Conversa, Avaliações and Perfil
// (SPEC-27); a sign-in opens Conversa, and a launch reopens the screen she
// was last on. This repo renders and routes; it decides nothing a back end
// owns.
//
// The PYF team's admin (SPEC-33) answers at /admin and on every path of an
// admin.* host, full width, outside the column. Its screens load on demand,
// so an athlete's phone never fetches them, and nothing in the athlete app
// links there.
//
// The athlete app speaks PT, FR or EN (SPEC-31), and so does the admin
// (SPEC-38): one provider holds both, with the same device memory and the
// same account setting, so one person has one language in both.

import { Suspense, lazy, useEffect, useState } from "react";
import type { ComponentType } from "react";

import type { SignIn as SignInCalls } from "./account/signIn";
import type { SignUp as SignUpCall } from "./account/signUp";
import { EMPTY_DRAFT } from "./account/signUpForm";
import type { AdminProps } from "./admin/Admin";
import { AdminLoading, AdminUnavailable } from "./admin/AdminFallback";
import type { AdminSource } from "./admin/adminSource";
import { isAdminHost } from "./admin/host";
import { AssessmentFlow } from "./assessment/AssessmentFlow";
import { Assessments } from "./screens/Assessments";
import { Conversa } from "./screens/Conversa";
import { Legal } from "./screens/Legal";
import { NewPassword } from "./screens/NewPassword";
import { Profile } from "./screens/Profile";
import { ProfileOnboarding } from "./screens/ProfileOnboarding";
import { SignIn } from "./screens/SignIn";
import { SignUp } from "./screens/SignUp";
import { Splash } from "./screens/Splash";
import { Welcome } from "./screens/Welcome";
import type { SessionCheck } from "./entry/session";
import { LanguageProvider } from "./i18n/LanguageProvider";
import { rememberPlace } from "./lib/place";
import { paths, useRouter } from "./lib/router";
import type { Onboarding } from "./onboarding/onboarding";
import type { ProfileSession } from "./profile/profile";
import type { AccountLanguage } from "./settings/settings";

export interface AppProps {
  checkSession: SessionCheck;
  signUp: SignUpCall;
  signIn: SignInCalls;
  onboarding: Onboarding;
  profile: ProfileSession;
  admin: AdminSource;
  /** SPEC-31: where the account's language is read and saved. */
  settings: AccountLanguage;
}

const Admin = lazy<ComponentType<AdminProps>>(() =>
  import("./admin/Admin").catch((error: unknown) => {
    console.error("[admin] screens not loaded", { kind: error instanceof Error ? error.name : typeof error });
    return { default: AdminUnavailable };
  }),
);

function Screen({ checkSession, signUp, signIn, onboarding, profile }: AppProps) {
  const { path } = useRouter();
  // Above the routes, so the Termos and back keep what was typed.
  const [signUpDraft, setSignUpDraft] = useState(EMPTY_DRAFT);

  // Where the app reopens next launch: a tab or a questionnaire, never a sign-in screen.
  useEffect(() => rememberPlace(path), [path]);

  switch (path) {
    case paths.welcome:
      return <Welcome />;
    case paths.signUp:
      return <SignUp signUp={signUp} draft={signUpDraft} onDraftChange={setSignUpDraft} />;
    case paths.terms:
      return <Legal document="terms" />;
    case paths.privacy:
      return <Legal document="privacy" />;
    case paths.signIn:
      return <SignIn signIn={signIn} />;
    case paths.newPassword:
      return <NewPassword signIn={signIn} />;
    case paths.profileOnboarding:
      return <ProfileOnboarding onboarding={onboarding} />;
    case paths.profileEdit:
      return <ProfileOnboarding onboarding={onboarding} returnTo={paths.profile} />;
    case paths.conversa:
      return <Conversa source={onboarding} />;
    case paths.assessments:
      return <Assessments source={onboarding} />;
    case paths.profile:
      return <Profile session={profile} />;
    case paths.iciq:
      return <AssessmentFlow assessmentId="iciq" source={onboarding} />;
    case paths.cycleSymptoms:
      return <AssessmentFlow assessmentId="ciclo-sintomas" source={onboarding} />;
    case paths.cycleSport:
      return <AssessmentFlow assessmentId="ciclo-desporto" source={onboarding} />;
    case paths.leafq:
      return <AssessmentFlow assessmentId="leafq" source={onboarding} />;
    case paths.sleep:
      return <AssessmentFlow assessmentId="sono" source={onboarding} />;
    case paths.beda:
      return <AssessmentFlow assessmentId="beda" source={onboarding} />;
    case paths.ciss:
      return <AssessmentFlow assessmentId="ciss" source={onboarding} />;
    case paths.fonseca:
      return <AssessmentFlow assessmentId="fonseca" source={onboarding} />;
    default:
      // The launch path, and any path the app does not know: the Splash
      // checks the session and sends the athlete to the right screen.
      return <Splash checkSession={checkSession} />;
  }
}

export default function App(props: AppProps) {
  const { path } = useRouter();
  const admin = path === paths.admin || isAdminHost();
  return (
    // Above the routes and the admin, so a change of language keeps the screen and its state (SPEC-31, SPEC-38).
    <LanguageProvider account={props.settings}>
      {admin ? (
        // The admin's wait speaks the language too: AdminFallback is in this chunk, the screens in their own.
        <Suspense fallback={<AdminLoading />}>
          <Admin source={props.admin} />
        </Suspense>
      ) : (
        // On a desktop the app keeps a phone's width, centred (SPEC-12, Dev notes).
        <div className="mx-auto min-h-dvh w-full max-w-[430px] bg-cream">
          <Screen {...props} />
        </div>
      )}
    </LanguageProvider>
  );
}
